<template>
  <div id="projectLists">
    <el-row class="header-row">
      <el-col :span="24">
        <h3 class="el-page-header">Projects</h3>
        <el-breadcrumb separator="/">
          <el-breadcrumb-item :to="{ path: '/' }">Home</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: '/projects' }">Projects</el-breadcrumb-item>
          <el-breadcrumb-item :to="{ path: `/projects/${project.id}` }">{{ project.projectID }}</el-breadcrumb-item>
        </el-breadcrumb>
      </el-col>
    </el-row>
    <div v-loading="loading" v-if="loading" style="height: 120px" element-loading-spinner="atom-audit-loader"></div>
    <el-card v-show="!loading">
      <el-tabs v-model="activeName" @tab-click="handleTabClick">
        <el-tab-pane label="Project" name="details" style="min-height: 400px" key="details">
          <el-form v-if="!loading" size="mini" label-position="left" label-width="140px">
            <el-row :gutter="20">
              <el-col :span="16">
                <h4 class="headerWithBorder">Details</h4>
                <el-row :gutter="20">
                  <el-col :lg="12" :md="24">
                    <el-form-item size="mini" label="Type">
                      <hia-el-select :disabled="true" v-model="projectTypeId" placeholder="Project Type" :clearable="false" :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.projectTypes.list" v-on:input="updateProject()"></hia-el-select>
                    </el-form-item>

                    <el-form-item size="mini" label="ID">
                      <div :class="!projectManagement() ? 'el-input el-input--mini is-disabled' : 'el-input el-input--mini'">
                        <input id="projectID" :class="'el-input__inner'" :disabled="!projectManagement()" maxlength="200" type="text" :value="projectID" @change="putProjectId($event, 'projectID')" />
                      </div>
                    </el-form-item>

                    <el-form-item prop="name" label="Process">
                      <hia-el-select placeholder="Process" :disabled="!projectManagement()" :value="project.processId" clearable :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.projectProcesses.list" @input="updateProjectProcess($event)"></hia-el-select>
                    </el-form-item>

                    <el-form-item size="mini" label="Tags">
                      <el-select class="multiSelect" :disabled="!projectManagement()" size="mini" v-model="identifierSelectionsArray" filterable multiple collapse-tags placeholder="Tags" @visible-change="tagsSelectHideShow($event, project)" @remove-tag="tagsSelectRemoveTag($event, project)">
                        <el-option v-for="item in dropdowns.projectIdentifiers.list" :key="item.key" :label="item.value" :value="item.key" :disabled="item.inactive"> </el-option>
                      </el-select>
                    </el-form-item>

                    <el-form-item size="mini" label="Description">
                      <textarea :disabled="!projectManagement()" style="width: 100%" class="el-textarea__inner" type="textarea" :rows="2" id="comments" placeholder="Description" v-model.lazy="description" @change="updateProject('description')"></textarea>
                    </el-form-item>
                  </el-col>
                  <el-col :lg="12" :md="24">
                    <el-form-item v-if="tenantSetup.enableLevel2Audits && (level2Auditor() || projectManagement())" size="mini" label="Project Review Type" prop="projectReviewTypeId">
                      <hia-el-select :disabled="!projectManagement()" v-model="projectReviewTypeId" @input="updateProject('projectReviewTypeId')" placeholder="Project Review Type" :clearable="false" :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.projectReviewTypes.list"></hia-el-select>
                    </el-form-item>
                    <el-form-item size="mini" label="Start Date">
                      <el-date-picker :disabled="!projectManagement()" size="mini" type="date" v-model="startDate" id="startDate" format="MM/dd/yyyy" @change="updateProject('startDate')" :clearable="false"> </el-date-picker>
                    </el-form-item>
                    <el-form-item size="mini" label="End Date">
                      <el-date-picker :disabled="!projectManagement()" size="mini" type="date" v-model="endDate" id="endDate" format="MM/dd/yyyy" @change="updateProject('endDate')"> </el-date-picker>
                    </el-form-item>
                    <el-form-item size="mini" label="Status">
                      <hia-el-select :disabled="!projectManagement()" v-model="projectStatusId" placeholder="Project Status" :clearable="false" :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.projectStatus.list" v-on:input="updateProject('projectStatusId')"></hia-el-select>
                    </el-form-item>
                    <el-form-item size="mini" label="Lead" prop="leadId">
                      <hia-el-select :disabled="!projectManagement()" v-model="leadId" placeholder="Lead" filterable clearable itemkey="key" label="value" :dropdowns="dropdowns.allAuditorUsers.list" v-on:input="updateProject('leadId')"></hia-el-select>
                    </el-form-item>
                    <el-form-item size="mini" label="Created By">
                      <el-input size="mini" id="userId" :value="getUserById(project.userId)" :disabled="true"> </el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-col>
              <el-col :span="8">
                <el-row>
                  <el-col :span="24">
                    <h4 class="headerWithBorder">
                      Project Defaults
                      <el-tooltip effect="dark" content="These defaults will be used for new records created in this project. Record imports will override defaults when the applicable field is populated in the import file, otherwise, the default is used." placement="top">
                        <i class="el-icon-info" style="font-size: 11px"></i>
                      </el-tooltip>
                    </h4>
                    <el-form-item size="mini" label="Default Facility">
                      <hia-el-select :disabled="!projectManagement()" v-model="clientId" placeholder="Default Facility" clearable :filterable="true" itemkey="key" label="value" :dropdowns="dropdowns.clients.list" v-on:input="updateProject('clientId')"></hia-el-select>
                    </el-form-item>

                    <el-form-item v-if="['Professional Fee', 'Outpatient', 'Ambulatory'].includes(project.projectTypeName)" size="mini" label="Default Code Type">
                      <hia-el-select :disabled="!projectManagement()" @input="updateProject('defaultCodeType')" v-model="defaultCodeType" placeholder="Default Code Type" clearable itemkey="key" label="value" :dropdowns="dropdowns.codeTypes.list" filterable> </hia-el-select>
                    </el-form-item>

                    <el-form-item size="mini" label="Default Provider">
                      <providerSelector :disabled="!projectManagement()" :provider="project.defaultProvider" @selected="handleDefaultProviderSelected" v-model="defaultProviderId" placeholder="Default Provider" @input="updateProject('defaultProviderId')"></providerSelector>
                    </el-form-item>

                    <el-form-item size="mini" label="Default Coder">
                      <coderSelector :disabled="!projectManagement()" :clientcoder="project.defaultCoder" @selected="handleDefaultCoderSelected" v-model="defaultCoderId" placeholder="Default Coder" @input="updateProject('defaultCoderId')"></coderSelector>
                    </el-form-item>

                    <el-form-item v-if="tenantSetup && tenantSetup.activityAuditsEnabled && hasActivityAuditTypesForCurrentProject" size="mini" label="Enable Activity Audits">
                      <el-switch :disabled="!projectManagement()" v-model="project.activityAuditsEnabled" @change="updateProject('activityAuditsEnabled')"></el-switch>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-col>
            </el-row>
          </el-form>

          <el-collapse v-model="activeNames" style="margin-top: 10px">
            <el-collapse-item style="margin-top: 20px" title="Project Tracking" name="tracking">
              <template slot="title">
                <span style="width: 100%">
                  Project Tracking
                  <span class="codeTotalHeader">
                    {{ project.processId ? ` - ${getProcessName(project.processId)}` : ' - No Process Selected' }}
                  </span>
                </span>
              </template>

              <el-row style="margin-top: 20px" v-if="!loading">
                <el-col :span="24">
                  <v-client-table id="taskTable" class="hiaTable" :columns="['task', 'taskDate', 'isNA', 'modifiedBy', 'modifiedDate']" :data="project.projectTasks.filter((x) => x.task.processId === selectedProcess)" :options="taskTableOptions">
                    <template slot="task" slot-scope="props">
                      {{ props.row.task ? props.row.task.name : '' }}
                    </template>
                    <template slot="taskDate" slot-scope="props">
                      <el-date-picker :disabled="props.row.isNA || !projectManagement()" style="max-width: 150px; padding: 0 0 0 5px; height: 20px; margin-right: 20px" v-model="props.row.taskDate" @input="putProjectTask(props.row)" format="MM/dd/yyyy"></el-date-picker>
                    </template>
                    <template slot="isNA" slot-scope="props">
                      <el-checkbox :disabled="!projectManagement()" v-model="props.row.isNA" @input="putProjectTask(props.row)"></el-checkbox>
                    </template>
                    <template slot="modifiedBy" slot-scope="props">
                      {{ getUserById(props.row.modifiedBy) }}
                    </template>
                    <template slot="modifiedDate" slot-scope="props">
                      {{ props.row.modifiedDate ? format(parseISO(props.row.modifiedDate), 'M/d/yyyy hh:mm a') : '' }}
                      <i style="cursor: pointer" @click="showHistoryPopper($event, 'taskHistoryPopover', props.row)" v-if="props.row.taskHistory && props.row.taskHistory.length > 0" class="mdi mdi-history"></i>
                    </template>
                  </v-client-table>
                </el-col>
              </el-row>
            </el-collapse-item>

            <el-collapse-item title="Dashboard" name="dashboard">
              <div style="margin: 20px 0px 0px 0px" v-if="!loading">
                <dashboards :projectId="project.id" :drgTableIds="drgTableIds" :proRecordIds="proRecordIds" :ascTableIds="ascTableIds" :type="compDashboardType" :codeTypes="codeTypeFilters" :dropdowns="dropdowns" :isReportSection="false" :showLevel2Audit="project.projectReviewTypeId === 2">
                  <template v-slot:filters>
                    <el-col :span="2">
                      <i title="Filter dashboards based on record status" class="mdi mdi-filter-variant filterIcon" style="margin: 4px 0px 0px 10px; font-size: 18px"></i>
                    </el-col>
                    <el-col :span="11">
                      <el-select class="multiSelect" :title="getMultiSelectTitle(statusFilters, dropdowns.reviewStatus.list)" :collapse-tags="true" size="mini" v-model="statusFilters" filterable multiple @visible-change="statusSelectHideShow($event, project)" @remove-tag="statusSelectRemoveTag($event, project)">
                        <el-option v-for="item in dropdowns.reviewStatus.list" :key="item.key" :label="item.value" :value="item.key" :disabled="item.inactive"> </el-option>
                      </el-select>
                    </el-col>
                    <el-col :span="11">
                      <el-select v-if="project.projectTypeName !== 'Inpatient'" :title="getMultiSelectTitle(codeTypeFilters, dropdowns.codeTypes.list)" :collapse-tags="true" class="multiSelect" style="margin-left: 5px" size="mini" v-model="codeTypeFilters" filterable multiple @visible-change="codeTypesSelectHideShow($event, project)" @remove-tag="statusSelectRemoveTag($event, project)">
                        <el-option v-for="item in dropdowns.codeTypes.list" :key="item.key" :label="item.value" :value="item.key" :disabled="item.inactive"> </el-option>
                      </el-select>
                    </el-col>
                  </template>
                  <template v-slot:stats>
                    <center class="stats">
                      <el-badge class="item" :class="{ 'stats-loading': loading }" :value="compProjectStatistics.allRecords">
                        <el-tag type="primary" size="small">All Records</el-tag>
                      </el-badge>
                      <el-badge class="item" :class="{ 'stats-loading': loading }" :value="compProjectStatistics.notStarted">
                        <el-tag type="primary" size="small">Not Started</el-tag>
                      </el-badge>
                      <el-badge class="item" :class="{ 'stats-loading': loading }" :value="compProjectStatistics.inProgress">
                        <el-tag type="primary" size="small">In Progress</el-tag>
                      </el-badge>
                      <el-badge class="item" :class="{ 'stats-loading': loading }" :value="compProjectStatistics.preliminary">
                        <el-tag type="primary" size="small">Preliminary</el-tag>
                      </el-badge>
                      <el-badge class="item" :class="{ 'stats-loading': loading }" :value="compProjectStatistics.readyForCoder">
                        <el-tag type="primary" size="small">Ready For Coder</el-tag>
                      </el-badge>
                      <el-badge class="item" :class="{ 'stats-loading': loading }" :value="compProjectStatistics.completed">
                        <el-tag type="primary" size="small">Completed</el-tag>
                      </el-badge>
                    </center>
                  </template>
                </dashboards>
              </div>
            </el-collapse-item>
          </el-collapse>
        </el-tab-pane>

        <el-tab-pane v-if="project.projectTypeName === 'Inpatient'" label="Inpatient Charts" name="Inpatient" key="Inpatient">
          <inpatientList></inpatientList>
        </el-tab-pane>
        <el-tab-pane v-if="project.projectTypeName === 'Outpatient'" label="Outpatient Charts" name="Outpatient" key="Outpatient">
          <opList></opList>
        </el-tab-pane>
        <el-tab-pane v-if="project.projectTypeName === 'Ambulatory'" label="Ambulatory Charts" name="Ambulatory" key="Ambulatory">
          <opList></opList>
        </el-tab-pane>
        <el-tab-pane v-if="project.projectTypeName === 'Professional Fee'" label="Pro Charts" name="Professional Fee" key="Professional Fee">
          <proList></proList>
        </el-tab-pane>

        <el-tab-pane label="Rebuttal Tracking" name="RebuttalTracking" key="RebuttalTracking" lazy>
          <template v-if="!loading">
            <template v-if="project.projectTypeName === 'Inpatient'">
              <ipRebuttalsTrackingByOriginalAuditor v-if="project.projectReviewTypeId === 2" :drgTableIds="rebuttalDrgTableIds"></ipRebuttalsTrackingByOriginalAuditor>
              <ipRebuttalsTrackingByCoder v-else :drgTableIds="rebuttalDrgTableIds"></ipRebuttalsTrackingByCoder>
            </template>
            <template v-else-if="project.projectTypeName === 'Outpatient'">
              <opRebuttalsTrackingByOriginalAuditor v-if="project.projectReviewTypeId === 2" :ascTableIds="rebuttalAscTableIds"></opRebuttalsTrackingByOriginalAuditor>
              <opRebuttalsTrackingByCoder v-else :ascTableIds="rebuttalAscTableIds"></opRebuttalsTrackingByCoder>
            </template>
            <template v-else-if="project.projectTypeName === 'Ambulatory'">
              <ascRebuttalsTrackingByOriginalAuditor v-if="project.projectReviewTypeId === 2" :ascTableIds="rebuttalAscTableIds"></ascRebuttalsTrackingByOriginalAuditor>
              <ascRebuttalsTrackingByCoder v-else :ascTableIds="rebuttalAscTableIds"></ascRebuttalsTrackingByCoder>
            </template>
            <template v-else-if="project.projectTypeName === 'Professional Fee'">
              <proRebuttalsTrackingByOriginalAuditor v-if="project.projectReviewTypeId === 2" :proRecordIds="rebuttalProRecordIds"></proRebuttalsTrackingByOriginalAuditor>
              <proRebuttalsTrackingByCoder v-else :proRecordIds="rebuttalProRecordIds"></proRebuttalsTrackingByCoder>
            </template>
          </template>
        </el-tab-pane>

        <el-tab-pane v-if="proRiskAnalysis() && project.projectTypeName === 'Professional Fee'" label="Pro Risk Canvas" name="Canvas" lazy key="Canvas">
          <projectProRiskCanvas :projectId="project.id"></projectProRiskCanvas>
        </el-tab-pane>

        <el-tab-pane label="Record Import" name="importNew" lazy key="importNew">
          <recordImport :projectDropdownsNeeded="dropdownsNeeded"></recordImport>
        </el-tab-pane>

        <el-tab-pane v-if="project.projectTypeName === 'Inpatient' && project.projectReviewTypeId === 2" label="Level 2 Import" name="level2Import" lazy key="level2Import">
          <level2ImportInpatient :projectId="project.id"></level2ImportInpatient>
        </el-tab-pane>

        <el-tab-pane v-if="project.projectTypeName === 'Outpatient' && project.projectReviewTypeId === 2" label="Level 2 Import" name="level2ImportOutpatient" lazy key="level2ImportOutpatient">
          <level2ImportOutpatient :projectId="project.id"></level2ImportOutpatient>
        </el-tab-pane>

        <el-tab-pane v-if="project.projectTypeName === 'Ambulatory' && project.projectReviewTypeId === 2" label="Level 2 Import" name="level2ImportAsc" lazy key="level2ImportAsc">
          <level2ImportAsc :projectId="project.id"></level2ImportAsc>
        </el-tab-pane>

        <el-tab-pane v-if="project.projectTypeName === 'Professional Fee' && project.projectReviewTypeId === 2" label="Level 2 Import" name="level2ImportPro" lazy key="level2ImportPro">
          <level2ImportPro :projectId="project.id"></level2ImportPro>
        </el-tab-pane>
      </el-tabs>
    </el-card>

    <el-popover ref="taskHistoryPopover" :title="`Change History for ${selectedTaskHistoryTask.name}`" placement="right" width="600" @after-enter="handleHidePopOver('taskHistoryPopover')">
      <div style="align-items: center">
        <v-client-table id="taskTable" class="hiaTable" :columns="['event', 'date', 'user']" :data="selectedTaskHistoryRows" :options="taskTableOptions"> </v-client-table>
      </div>
    </el-popover>
    <el-popover ref="savePopover" style="word-break: normal" placement="bottom" title="" width="300" trigger="manual" popper-class="popover" :content="savePopoverContent" @after-enter="handleHidePopOver('savePopover')"> </el-popover>
  </div>
</template>

<script>
  import { mapState, mapActions, mapMutations, mapGetters } from 'vuex'
  import { createHelpers } from 'vuex-map-fields'
  import InpatientList from '@/views/reviews/inpatient/inpatientList'
  import proList from '@/views/reviews/pro/proList'
  import dateHelper from '@/mixins/date-helper'
  import recordImport from '@/views/projects/recordImport'
  import dashboards from '@/views/reports/dashboards'
  import opList from '@/views/reviews/outpatient/opList'
  import HiaAuthorization from '@/mixins/hiaAuthorization'
  import { format, parseISO } from 'date-fns'
  import providerSelector from '@/components/controls/providerSelector'
  import coderSelector from '@/components/controls/coderSelector'
  import projectProRiskCanvas from '@/views/projects/projectProRiskCanvas'
  import level2ImportInpatient from '@/views/projects/level2ImportInpatient'
  import level2ImportOutpatient from '@/views/projects/level2ImportOutpatient'
  import level2ImportAsc from '@/views/projects/level2ImportAsc'
  import level2ImportPro from '@/views/projects/level2ImportPro'
  import ipRebuttalsTrackingByCoder from '@/views/projects/rebuttalTracking/IP_RebuttalsTrackingByCoder'
  import opRebuttalsTrackingByCoder from '@/views/projects/rebuttalTracking/OP_RebuttalsTrackingByCoder'
  import ascRebuttalsTrackingByCoder from '@/views/projects/rebuttalTracking/ASC_RebuttalsTrackingByCoder'
  import proRebuttalsTrackingByCoder from '@/views/projects/rebuttalTracking/PRO_RebuttalsTrackingByCoder'
  import ipRebuttalsTrackingByOriginalAuditor from '@/views/projects/rebuttalTracking/IP_RebuttalsTrackingByOriginalAuditor'
  import opRebuttalsTrackingByOriginalAuditor from '@/views/projects/rebuttalTracking/OP_RebuttalsTrackingByOriginalAuditor'
  import ascRebuttalsTrackingByOriginalAuditor from '@/views/projects/rebuttalTracking/ASC_RebuttalsTrackingByOriginalAuditor'
  import proRebuttalsTrackingByOriginalAuditor from '@/views/projects/rebuttalTracking/PRO_RebuttalsTrackingByOriginalAuditor'

  const { mapFields } = createHelpers({
    getterType: 'managedLists/projects/getField',
    mutationType: 'managedLists/projects/updateField'
  })

  export default {
    name: 'project',
    mixins: [dateHelper, HiaAuthorization],
    data() {
      return {
        format: format,
        parseISO: parseISO,
        activeName: 'details',
        activeNames: ['tracking', 'dashboard'],
        statusFilters: [5, 3, 4],
        codeTypeFilters: [],
        backupCodeTypeFilters: [],
        backupStatusFilters: [],
        backupIdentifierSelectionsArray: [],
        deactivated: false,
        loading: false,
        selectedProcess: null,
        selectedTaskHistoryRows: [],
        selectedTaskHistoryTask: {},
        dropdownsNeeded: ['clients', 'projectIdentifiers', 'projectTypes', 'coders', 'allAuditorUsers', 'inpatientAuditors', 'outpatientAuditors', 'proAuditors', 'ascAuditors', 'codeTypes', 'outpatientGroupers', 'reviewStatus', 'projectStatus', 'projectProcesses', 'projectReviewTypes', 'activityAuditTypesIP', 'activityAuditTypesOP', 'activityAuditTypesAMB', 'activityAuditTypesPro'],
        localdateRange: [],
        taskTableOptions: {
          filterable: false,
          filterByColumn: false,
          sortable: ['name'],
          columnsClasses: {
            task: 'taskCol',
            taskDate: 'taskDateCol',
            isNA: 'isNACol',
            modifiedBy: 'modifiedByCol',
            modifiedDate: 'modifiedDateCol'
          },
          headings: {
            taskDate: 'Task Date',
            modifiedBy: 'Modified By',
            isNA: 'N/A',
            modifiedDate: 'Modified Date'
          },
          sortIcon: { is: 'none' }
        },
        savePopoverContent: ''
      }
    },
    methods: {
      ...mapActions('inpatientReviews/', ['GET_IPLIST_PROJECT_STATS']),
      ...mapActions('outpatientReviews/', ['GET_OPLIST_PROJECT_STATS']),
      ...mapActions('proReviews/', ['GET_PROLIST_PROJECT_STATS']),
      ...mapActions('proReviews/canvas/', ['GET_CANVAS_LIST']),
      ...mapActions('dropdowns/', ['GET_DROPDOWNS']),
      ...mapActions('managedLists/tenantSetup/', ['GET_TENANT_SETUP']),
      ...mapActions('managedLists/projects/', ['LOAD_PROJECT', 'UPDATE_PROJECT', 'PUT_PROJECT_TASK', 'GET_PROJECT_DASHBOARD_IDS_STATS', 'GET_PROJECT_REBUTTAL_TRACKING_IDS']),
      ...mapMutations('managedLists/projects/', ['SET_PROJECTPK', 'SET_PROJECT']),
      getUserById(userId) {
        let user = this.dropdowns.allAuditorUsers.list.find((f) => f.key === userId)
        if (user) {
          user = user.value
          return user
        }
        return user
      },
      getMultiSelectTitle(optionsSelected, list) {
        return optionsSelected
          .map((x) => {
            const listRow = list.find((y) => y.key === x)
            return listRow.value
          })
          .join('\n')
      },
      initialize() {
        window.document.title = 'Project'
        this.loading = true
        const payload = {
          id: this.$route.params.id,
          statusFilters: this.statusFilters,
          codeTypeFilters: []
        }

        // we need to make sure that we load the project before we render the associated tabs for IP, OP and Pro Lists
        // because of the way the watchers work inside those components

        this.GET_DROPDOWNS(this.dropdownsNeeded)
          .then(() => {
            this.codeTypeFilters = []
            this.dropdowns.codeTypes.list.forEach((a) => this.codeTypeFilters.push(a.key))
            payload.codeTypeFilters = this.codeTypeFilters
            Promise.all([this.GET_TENANT_SETUP(), this.LOAD_PROJECT(payload)])
              .then(() => {
                this.selectedProcess = this.project.processId
                if (!this.selectedProcess) {
                  this.activeNames = this.activeNames.filter((x) => x !== 'tracking')
                }

                this.$nextTick(() => {
                  this.loading = false
                  this.setActiveTabName()
                })
              })
              .catch((error) => {
                this.loading = false
                const errorMessage = error.response?.data?.message || error.message || 'Error loading project'
                this.$message({
                  message: errorMessage,
                  type: 'error'
                })
              })
          })
          .catch((error) => {
            this.loading = false
            const errorMessage = error.response?.data?.message || error.message || 'Error loading dropdowns'
            this.$message({
              message: errorMessage,
              type: 'error'
            })
          })
      },
      handleTabClick(e) {
        if (this.activeName === 'details') {
          this.refreshProjectRecordIds()
        }
      },
      refreshProjectRecordIds() {
        const payload = {
          id: this.id,
          statusFilters: this.statusFilters,
          projectTypeName: this.project.projectTypeName,
          codeTypeFilters: this.codeTypeFilters
        }
        this.GET_PROJECT_DASHBOARD_IDS_STATS(payload)
        this.GET_PROJECT_REBUTTAL_TRACKING_IDS({
          id: this.id,
          projectTypeName: this.project.projectTypeName,
          codeTypeFilters: this.codeTypeFilters
        })
      },
      async putProjectTask(updatedRow) {
        const index = this.project.projectTasks.findIndex((x) => x.projectsTaskId === updatedRow.projectsTaskId)

        if (updatedRow.isNA && updatedRow.taskDate) {
          try {
            await this.$confirm('This will clear the existing Task Date. Continue?', 'Warning', { confirmButtonText: 'OK', cancelButtonText: 'Cancel', type: 'warning' })
            updatedRow.taskDate = null
          } catch (e) {
            updatedRow.isNA = !updatedRow.isNA
            this.$set(this.project.projectTasks, index, updatedRow)
            return
          }
        }

        // we found a bug where element's datepicker X button calls this method twice
        // so the following code prevents the network call from happening twice
        const existingRow = this.project.projectTasks[index]
        if (JSON.stringify(updatedRow) === JSON.stringify(existingRow)) {
          return
        }

        this.$set(this.project.projectTasks, index, updatedRow)

        try {
          await this.PUT_PROJECT_TASK(updatedRow)
          this.$message({
            message: 'Project Task Saved',
            type: 'success'
          })
        } catch (err) {
          this.$message({
            message: 'Error Editing Project Task',
            type: 'error'
          })
        }
      },
      handleDefaultProviderSelected(provider) {
        if (provider.providerId) {
          this.project.defaultProvider = provider
        } else {
          this.project.defaultProvider = null
        }
      },
      handleDefaultCoderSelected(coder) {
        if (coder.coderId) {
          this.project.defaultCoder = coder
        } else {
          this.project.defaultCoder = null
        }
      },
      setActiveTabName() {
        // because of keep alive, we may need to reset the active tab if the user goes from in IP project to an OP or Pro project
        if (this.activeName !== 'details' && this.activeName !== this.project.projectTypeName) {
          this.activeName = 'details'
        }
      },
      async updateProjectProcess(val) {
        this.project.processId = parseInt(val)
        await this.updateProject('taskTemplate')
        if (this.project.processId && !this.activeNames.some((x) => x === 'tracking')) {
          this.activeNames.push('tracking')
        }
        this.selectedProcess = this.project.processId
      },
      async updateProject(field) {
        try {
          if (field === 'projectStatusId') {
            switch (this.project.projectStatusId) {
              case 1:
                this.project.projectColor = '#003169'
                break
              case 2:
                this.project.projectColor = '#fec41f'
                break
              case 3:
                this.project.projectColor = '#63b344'
                break
            }
          }
          await this.UPDATE_PROJECT(this.project)

          this.$message({
            message: 'Project Updated Successfully',
            type: 'success'
          })
        } catch (err) {
          this.$message({
            message: err.message ? `Project Update Error: ${err.message}` : 'Project Update Error',
            type: 'error'
          })
        }
      },
      getProcessName(processId) {
        return this.GET_VALUEFROMKEY('projectProcesses', processId)
      },
      showHistoryPopper(e, refName, row) {
        this.selectedTaskHistoryRows = []

        // copy the history and stick the current row in as the first history item
        // so that the popover displays current + all history
        const history = JSON.parse(JSON.stringify(row.taskHistory))
        history.unshift(row)

        this.selectedTaskHistoryTask = row.task

        for (let i = 0; i < history.length; i++) {
          const currentRow = history[i]
          const olderRow = history[i + 1]

          if (olderRow) {
            if (currentRow.isNA !== olderRow.isNA) {
              if (currentRow.isNA) {
                this.selectedTaskHistoryRows.push({
                  event: 'N/A was checked',
                  date: format(parseISO(currentRow.modifiedDate), 'M/d/yyyy hh:mm a'),
                  user: this.getUserById(currentRow.modifiedBy)
                })
              } else {
                this.selectedTaskHistoryRows.push({
                  event: 'N/A was unchecked',
                  date: format(parseISO(currentRow.modifiedDate), 'M/d/yyyy hh:mm a'),
                  user: this.getUserById(currentRow.modifiedBy)
                })
              }
            }

            if (currentRow.taskDate !== olderRow.taskDate) {
              this.selectedTaskHistoryRows.push({
                event: `Task Date was changed from ${olderRow.taskDate ? format(parseISO(olderRow.taskDate), 'M/d/yyyy') : 'blank'} to ${currentRow.taskDate ? format(parseISO(currentRow.taskDate), 'M/d/yyyy') : 'blank'}`,
                date: format(parseISO(currentRow.modifiedDate), 'M/d/yyyy hh:mm a'),
                user: this.getUserById(currentRow.modifiedBy)
              })
            }
          } else {
            if (currentRow.isNA) {
              this.selectedTaskHistoryRows.push({
                event: 'N/A was checked',
                date: format(parseISO(currentRow.modifiedDate), 'M/d/yyyy hh:mm a'),
                user: this.getUserById(currentRow.modifiedBy)
              })
            } else {
              this.selectedTaskHistoryRows.push({
                event: `Task Date was set to ${currentRow.taskDate ? format(parseISO(currentRow.taskDate), 'M/d/yyyy') : 'blank'}`,
                date: format(parseISO(currentRow.modifiedDate), 'M/d/yyyy hh:mm a'),
                user: this.getUserById(currentRow.modifiedBy)
              })
            }
          }
        }

        const popper = this.$refs[refName]
        popper.doDestroy()
        popper.doClose()

        this.$nextTick(() => {
          this.popperClickTarget = e.target
          popper.doDestroy()
          popper.referenceElm = e.target
          this.isOpen = true
          popper.doShow()
        })
      },
      handleHidePopOver(ref) {
        const popper = this.$refs[ref]
        const handler = (e) => {
          if (e.target !== popper.$el) {
            popper.doDestroy()
            popper.doClose()
          }
          document.removeEventListener('click', handler, false)
        }
        document.addEventListener('click', handler, false)
      },
      statusSelectHideShow(show) {
        if (show) {
          this.backupStatusFilters = this.statusFilters
        } else {
          if (JSON.stringify(this.backupStatusFilters) !== JSON.stringify(this.statusFilters)) {
            this.handleTabClick()
          }
        }
      },
      codeTypesSelectHideShow(show) {
        if (show) {
          this.backupCodeTypeFilters = this.codeTypeFilters
        } else {
          if (JSON.stringify(this.backupCodeTypeFilters) !== JSON.stringify(this.codeTypeFilters)) {
            this.handleTabClick()
          }
        }
      },
      statusSelectRemoveTag() {
        this.handleTabClick()
      },
      tagsSelectHideShow(show, project) {
        if (show) {
          this.backupIdentifierSelectionsArray = this.identifierSelectionsArray
        } else {
          if (JSON.stringify(this.backupIdentifierSelectionsArray) !== JSON.stringify(this.identifierSelectionsArray)) {
            this.identifiersOnChange(project)
          }
        }
      },
      tagsSelectRemoveTag(val, project) {
        this.identifiersOnChange(project)
      },
      identifiersOnChange(project) {
        project.identifierSelectionsArray.forEach((element) => {
          // if element doesn't exist in, add it with add flag
          const matchedElements = project.identifierSelections.some(function(i) {
            return i.identifierID === element
          })
          if (!matchedElements) {
            const newValue = {
              id: 0,
              identifierID: element,
              add: true
            }

            project.identifierSelections.push(newValue)
          }
        })

        project.identifierSelections.forEach((element) => {
          // if element doesn't exists in Array, set deleted flag
          if (!project.identifierSelectionsArray.includes(element.identifierID)) {
            element.delete = true
          }
        })

        this.updateProject(true)
      },
      putProjectId(event, field) {
        if (!event.target.value) {
          this.savePopoverContent = 'Project ID must always contain a value.'
          this.openSavePopover(event)
          event.target.value = this.project.projectID
          return false
        }
        this.project.projectID = event.target.value
        this.updateProject(field)
      },
      openSavePopover(e) {
        if (!e) {
          return
        }

        const popper = this.$refs.savePopover

        popper.doDestroy()
        popper.doClose()

        this.$nextTick(() => {
          this.popperClickTarget = e.target
          popper.doDestroy()
          popper.referenceElm = this.popperClickTarget
          this.isOpen = true
          popper.doShow()
        })
      }
    },
    deactivated: function() {
      this.loading = true
      this.deactivated = true
    },
    created: function() {
      this.initialize()
    },
    activated: function() {
      if (this.deactivated) {
        this.initialize()
      }
      this.deactivated = false
    },
    components: {
      InpatientList,
      dashboards,
      proList,
      opList,
      recordImport,
      providerSelector,
      coderSelector,
      projectProRiskCanvas,
      level2ImportInpatient,
      level2ImportOutpatient,
      level2ImportAsc,
      level2ImportPro,
      ipRebuttalsTrackingByCoder,
      opRebuttalsTrackingByCoder,
      ascRebuttalsTrackingByCoder,
      proRebuttalsTrackingByCoder,
      ipRebuttalsTrackingByOriginalAuditor,
      opRebuttalsTrackingByOriginalAuditor,
      ascRebuttalsTrackingByOriginalAuditor,
      proRebuttalsTrackingByOriginalAuditor
    },
    computed: {
      ...mapGetters('dropdowns', ['GET_VALUEFROMKEY']),
      ...mapState('inpatientReviews/', ['ipProjectStatistics']),
      ...mapState('outpatientReviews/', ['opProjectStatistics']),
      ...mapState('proReviews/', ['proProjectStatistics']),
      ...mapState('reports/', ['reports']),
      ...mapState('dropdowns/', ['dropdowns']),
      ...mapState('managedLists/tenantSetup/', ['tenantSetup']),
      ...mapState('managedLists/projects/', ['project', 'projectLoading', 'drgTableIds', 'proRecordIds', 'ascTableIds', 'rebuttalDrgTableIds', 'rebuttalProRecordIds', 'rebuttalAscTableIds']),
      ...mapFields(['project.clientId', 'project.startDate', 'project.endDate', 'project.identifierId', 'project.coderId', 'project.id', 'project.projectID', 'project.projectTypeId', 'project.leadId', 'project.identifierSelectionsArray', 'project.identifierSelections', 'project.defaultCodeType', 'project.description', 'project.outpatientGrouperId', 'project.projectColor', 'project.projectStatusId', 'project.coderReviewedDate', 'project.providerReviewedDate', 'project.responseCompleteDate', 'project.defaultProviderId', 'project.defaultCoderId', 'project.projectReviewTypeId']),
      compDashboardType() {
        if (this.project.projectTypeName === 'Inpatient') {
          return 'ip'
        }
        if (this.project.projectTypeName === 'Outpatient') {
          return 'op'
        }
        if (this.project.projectTypeName === 'Professional Fee') {
          return 'pro'
        }
        if (this.project.projectTypeName === 'Ambulatory') {
          return 'asc'
        }
        return null
      },
      isOutpatient() {
        if (this.dropdowns && this.dropdowns.projectTypes) {
          return this.dropdowns.projectTypes.list.some((x) => x.value === 'Outpatient' && x.key === this.projectTypeId)
        }
        return false
      },
      compProjectStatistics() {
        if (this.project.projectTypeName === 'Inpatient') {
          return this.ipProjectStatistics
        }
        if (this.project.projectTypeName === 'Outpatient') {
          return this.opProjectStatistics
        }
        if (this.project.projectTypeName === 'Professional Fee') {
          return this.proProjectStatistics
        }
        if (this.project.projectTypeName === 'Ambulatory') {
          return this.opProjectStatistics
        }
        return {}
      },
      /** Dropdown name for GET_DROPDOWNS (see InpatientReview, OutpatientReview, proReview). */
      activityAuditTypesListKey() {
        if (!this.project || !this.project.projectTypeName) return null
        const byType = {
          Inpatient: 'activityAuditTypesIP',
          Outpatient: 'activityAuditTypesOP',
          Ambulatory: 'activityAuditTypesAMB',
          'Professional Fee': 'activityAuditTypesPro'
        }
        return byType[this.project.projectTypeName] || null
      },
      hasActivityAuditTypesForCurrentProject() {
        const k = this.activityAuditTypesListKey
        if (!k) return false
        return (this.dropdowns[k]?.list?.length ?? 0) > 0
      }
    }
  }
</script>

<style scoped>
  #comments::placeholder {
    font-size: 12px;
    font-family: 'Roboto', sans-serif;
  }

  .projectButtons {
    margin-top: 40px;
  }

  .projectButtons button {
    margin-left: 10px;
  }

  >>> #ipSummaryTitle {
    display: none;
  }

  .multiSelect >>> .el-input__inner {
    min-height: 30px !important;
  }

  #taskTable >>> .VuePagination {
    display: none;
  }

  >>> .taskCol {
    width: 20%;
  }

  >>> .taskDateCol {
    width: 15%;
  }

  >>> .isNACol {
    width: 10%;
  }

  >>> .modifiedByCol {
    width: 20%;
  }

  >>> .modifiedDatecol {
    width: 20%;
  }

  @media screen and (max-width: 1310px) and (min-width: 0px) {
    >>> .taskCol {
      width: 25%;
    }

    >>> .taskDateCol {
      width: 25%;
    }

    >>> .isNACol {
      width: 20%;
    }

    >>> .modifiedByCol {
      width: 15%;
    }

    >>> .modifiedDatecol {
      width: 15%;
    }
  }
</style>
